import { BadgeState, ProjectLabel, TaskViewType, TodoLabel } from 'yo-services/yolabs/services/poplarfrontend/enums_pb';
import { Badge } from '@yoweb/ui/components/indicators';
import { useIntlMeta } from '@yoweb/ui/hooks/useIntlMeta';
import { useUser } from '@poplar/hooks/useUser';
import { useTranslation } from '@yoweb/next-i18next';
import { convertTimeProtoToTimestamp } from '@yoweb/utils/converters';
import { formatDate, isDateToday, isDateYesterday } from '@yoweb/utils/date';
import { jsx as _jsx } from "react/jsx-runtime";

var getBadgeTimestamp = function getBadgeTimestamp(date, _ref) {
  var t = _ref.t,
      locale = _ref.locale,
      timeZone = _ref.timeZone;

  if (isDateToday(date)) {
    return t('dates.today-without-time', {
      ns: 'common'
    });
  }

  if (isDateYesterday(date)) {
    return t('dates.yesterday', {
      ns: 'common'
    });
  }

  var currentDate = new Date(date);
  var todayDate = new Date();

  if (currentDate.getFullYear() === todayDate.getFullYear()) {
    return formatDate(currentDate, {
      locale: locale,
      timeZone: timeZone,
      preset: 'shortMonthAndDay'
    });
  }

  return formatDate(currentDate, {
    locale: locale,
    timeZone: timeZone,
    preset: 'shortMonthYearAndDay'
  });
};

var statusIndicatorColorByType = function statusIndicatorColorByType(_ref2) {
  var badge = _ref2.badge,
      projectLabel = _ref2.projectLabel,
      todoLabel = _ref2.todoLabel,
      taskView = _ref2.taskView;

  if ((badge === null || badge === void 0 ? void 0 : badge.type) === BadgeState.BADGE_STATE_READY_FOR_GENERAL_FEEDBACK) {
    return 'successLight';
  }

  if (projectLabel) {
    switch (projectLabel) {
      case ProjectLabel.PROJECT_LABEL_COMPLETE:
        return 'successLight';

      case ProjectLabel.PROJECT_LABEL_MEMBER_HAS_THE_BATON:
        return 'criticalLight';

      case ProjectLabel.PROJECT_LABEL_REP_HAS_THE_BATON:
        return 'infoLight';

      case ProjectLabel.PROJECT_LABEL_ON_HOLD:
      case ProjectLabel.PROJECT_LABEL_CANCELLED:
      default:
        return 'sand';
    }
  }

  switch (todoLabel) {
    case TodoLabel.TODO_LABEL_COMPLETE:
      return 'successLight';

    case TodoLabel.TODO_LABEL_MEMBER_HAS_THE_BATON:
      return taskView === TaskViewType.TASK_VIEW_TYPE_COMPLETED ? 'successLight' : 'criticalLight';

    case TodoLabel.TODO_LABEL_HANDED_OFF_TO_REP:
    case TodoLabel.TODO_LABEL_REP_HAS_THE_BATON:
      return 'infoLight';

    case TodoLabel.TODO_LABEL_PENDING_COMPLETE:
      return 'criticalLight';

    case TodoLabel.TODO_LABEL_ON_HOLD:
    case TodoLabel.TODO_LABEL_CANCELLED:
    case TodoLabel.TODO_LABEL_DRAFT:
    case TodoLabel.TODO_LABEL_UNSPECIFIED:
    default:
      return 'sand';
  }
};

var renderStatusTranslation = function renderStatusTranslation(statusKey, date, t, locale, timeZone) {
  var status = t(statusKey);

  if (!date) {
    return status;
  }

  return t('statusDotDate', {
    status: status,
    time: getBadgeTimestamp(date, {
      t: t,
      locale: locale,
      timeZone: timeZone
    })
  });
};

var taskLabelToDisplayString = function taskLabelToDisplayString(_ref3, repName, t, locale, timeZone) {
  var todoLabel = _ref3.todoLabel,
      badge = _ref3.badge,
      taskView = _ref3.taskView,
      completedTimestamp = _ref3.completedTimestamp,
      updatedTimestamp = _ref3.updatedTimestamp,
      handOffTimestamp = _ref3.handOffTimestamp,
      createdTimestamp = _ref3.createdTimestamp,
      bookmarkTemplateId = _ref3.bookmarkTemplateId;

  switch (todoLabel) {
    case TodoLabel.TODO_LABEL_CANCELLED:
      return renderStatusTranslation('statuses.canceled', updatedTimestamp, t, locale, timeZone);

    case TodoLabel.TODO_LABEL_COMPLETE:
      return renderStatusTranslation('statuses.completed', completedTimestamp, t, locale, timeZone);

    case TodoLabel.TODO_LABEL_HANDED_OFF_TO_REP:
      return renderStatusTranslation('statuses.handed-off', handOffTimestamp, t, locale, timeZone);

    case TodoLabel.TODO_LABEL_MEMBER_HAS_THE_BATON:
      if ((badge === null || badge === void 0 ? void 0 : badge.type) === BadgeState.BADGE_STATE_READY_FOR_GENERAL_FEEDBACK || taskView === TaskViewType.TASK_VIEW_TYPE_COMPLETED) {
        return renderStatusTranslation('statuses.completed', completedTimestamp, t, locale, timeZone);
      }

      return t('awaiting-your-review');

    case TodoLabel.TODO_LABEL_PENDING_COMPLETE:
      return t('awaiting-your-review');

    case TodoLabel.TODO_LABEL_ON_HOLD:
      return t('on-hold');

    case TodoLabel.TODO_LABEL_REP_HAS_THE_BATON:
      return t('statuses.rep-is-on-it', {
        name: repName
      });

    case TodoLabel.TODO_LABEL_DRAFT:
      return renderStatusTranslation(bookmarkTemplateId ? 'statuses.bookmarked' : 'statuses.draft', createdTimestamp, t, locale, timeZone);

    case TodoLabel.TODO_LABEL_UNSPECIFIED:
    default:
      return null;
  }
};

var projectLabelToDisplayString = function projectLabelToDisplayString(_ref4, repName, t, locale, timeZone) {
  var projectLabel = _ref4.projectLabel,
      completedTimestamp = _ref4.completedTimestamp,
      updatedTimestamp = _ref4.updatedTimestamp;

  switch (projectLabel) {
    case ProjectLabel.PROJECT_LABEL_CANCELLED:
      return renderStatusTranslation('statuses.canceled', updatedTimestamp, t, locale, timeZone);

    case ProjectLabel.PROJECT_LABEL_COMPLETE:
      return renderStatusTranslation('statuses.completed', completedTimestamp, t, locale, timeZone);

    case ProjectLabel.PROJECT_LABEL_MEMBER_HAS_THE_BATON:
      return t('awaiting-your-review');

    case ProjectLabel.PROJECT_LABEL_ON_HOLD:
      return t('on-hold');

    case ProjectLabel.PROJECT_LABEL_REP_HAS_THE_BATON:
      return t('statuses.rep-is-on-it', {
        name: repName
      });

    case ProjectLabel.PROJECT_LABEL_UNSPECIFIED:
    default:
      return null;
  }
}; // Port of https://github.com/yolabs-io/aspen/blob/c4285df8e08811fc76137f9a4535621da0386b99/apps/oak/lib/src/tasklist/helper/tasklite_extensions.dart#L120


var getTaskStatusLabel = function getTaskStatusLabel(props, t, locale, timeZone) {
  var projectLabel = props.projectLabel,
      repName = props.repName;
  var representativeName = (repName === null || repName === void 0 ? void 0 : repName.preferredName) || (repName === null || repName === void 0 ? void 0 : repName.givenName) || t('task-intake-assistant', {
    ns: 'common'
  });

  if (projectLabel) {
    return projectLabelToDisplayString(props, representativeName, t, locale, timeZone);
  }

  return taskLabelToDisplayString(props, representativeName, t, locale, timeZone);
};

export var TaskStatusBadgeInner = function TaskStatusBadgeInner(props) {
  var _useIntlMeta = useIntlMeta(),
      locale = _useIntlMeta.locale;

  var _useUser = useUser(),
      timeZone = _useUser.timeZone;

  var _useTranslation = useTranslation('tasks'),
      t = _useTranslation.t;

  var actionTitle = getTaskStatusLabel(props, t, locale, timeZone);
  var actionBackgroundColor = statusIndicatorColorByType(props);

  if (!actionTitle) {
    return null;
  }

  return /*#__PURE__*/_jsx(Badge, {
    size: "xs",
    backgroundColor: actionBackgroundColor,
    type: "fill",
    borderRadius: "tiny",
    "data-testid": "task-action-badge-title",
    children: actionTitle
  });
};
export var TaskLiteStatusBadge = function TaskLiteStatusBadge(_ref5) {
  var _task$todoStatusMessa, _task$projectStatusMe, _convertTimeProtoToTi;

  var task = _ref5.task;
  var updatedTimestamp = convertTimeProtoToTimestamp(task.updatedTimestamp);
  return /*#__PURE__*/_jsx(TaskStatusBadgeInner, {
    todoLabel: (_task$todoStatusMessa = task.todoStatusMessage) === null || _task$todoStatusMessa === void 0 ? void 0 : _task$todoStatusMessa.todoLabel,
    projectLabel: (_task$projectStatusMe = task.projectStatusMessage) === null || _task$projectStatusMe === void 0 ? void 0 : _task$projectStatusMe.projectLabel,
    badge: task.taskBadge,
    createdTimestamp: convertTimeProtoToTimestamp(task.createdTimestamp),
    updatedTimestamp: updatedTimestamp,
    handOffTimestamp: convertTimeProtoToTimestamp(task.handOffTimestamp),
    completedTimestamp: (_convertTimeProtoToTi = convertTimeProtoToTimestamp(task.actualCompletionTimestamp)) !== null && _convertTimeProtoToTi !== void 0 ? _convertTimeProtoToTi : updatedTimestamp,
    bookmarkTemplateId: task.bookmarkTemplateId,
    repName: task.repName
  });
};
export var TaskStatusBadge = function TaskStatusBadge(_ref6) {
  var task = _ref6.task;
  return /*#__PURE__*/_jsx(TaskStatusBadgeInner, {
    todoLabel: task.todoStatusMessage,
    projectLabel: task.projectStatusMessage,
    badge: task.badge,
    updatedTimestamp: task.updated,
    createdTimestamp: task.created,
    handOffTimestamp: task.handoff,
    completedTimestamp: task.updated,
    bookmarkTemplateId: task.bookmarkTemplateId,
    repName: task.repName,
    taskView: task.taskView
  });
};